import type * as React from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';

import { table } from '@gitbook/doc-core';
import type { EditorContainer } from '@gitbook/slate-react';
import {
    Description,
    Dialog,
    DialogBody,
    DialogCloseButton,
    DialogFooter,
    DialogHeader,
    FieldError,
    Form,
    Heading,
    Label,
    Submit,
    TextField,
    TextInput,
} from '@gitbook/spine-react';
import type { OrderIndexPosition } from '@gitbook/util-orderindex';

import type { TableRecordKV } from '../../types';

export interface BulkInsertRowsDialogProps {
    editor: EditorContainer;
    tableKey: string;
    record: TableRecordKV;
    direction: 'above' | 'below';
    beforeOrderIndex?: string;
    afterOrderIndex?: string;
    isLastRow?: boolean;
    onClose: () => void;
}

type BulkInsertRowsFormInputs = {
    count: string;
};

const MAX_ROWS = 50;

/**
 * Dialog to insert multiple rows above or below a table row.
 * Capped at 50 rows to avoid performance issues.
 */
export function BulkInsertRowsDialog(props: BulkInsertRowsDialogProps): React.ReactElement {
    const {
        editor,
        tableKey,
        record,
        direction,
        beforeOrderIndex,
        afterOrderIndex,
        isLastRow,
        onClose,
    } = props;

    const form = useForm<BulkInsertRowsFormInputs>({
        values: { count: '1' },
    });

    const onSubmit: SubmitHandler<BulkInsertRowsFormInputs> = ({ count }) => {
        const countNumber = parseInt(count, 10);

        if (Number.isNaN(countNumber) || countNumber < 1 || countNumber > MAX_ROWS) {
            form.setError('count', {
                type: 'manual',
                message: `Please enter a number between 1 and ${MAX_ROWS}`,
            });
            return;
        }

        editor.change((change) => {
            const anchor: 'before' | 'after' = direction === 'above' ? 'after' : 'before';
            const position: OrderIndexPosition = {};

            if (direction === 'above') {
                position.after = record.value.orderIndex;
                if (beforeOrderIndex !== undefined) {
                    position.before = beforeOrderIndex;
                }
            } else {
                position.before = record.value.orderIndex;
                if (!isLastRow && afterOrderIndex !== undefined) {
                    position.after = afterOrderIndex;
                }
            }

            table.insertEmptyRecords(change, tableKey, {
                anchor,
                count: countNumber,
                position,
            });

            onClose();
        });
    };

    return (
        <Dialog>
            <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
                <DialogHeader>
                    <Heading>
                        Insert multiple rows {direction === 'above' ? 'upwards' : 'downwards'}
                    </Heading>
                    <Description>Add rows in bulk, up to {MAX_ROWS} rows at a time.</Description>
                </DialogHeader>
                <DialogBody>
                    <TextField
                        name="count"
                        required
                        validate={(value) => {
                            const num = parseInt(value, 10);
                            if (Number.isNaN(num) || num < 1) {
                                return 'Please enter a number greater than 0';
                            }
                            return true;
                        }}
                    >
                        <Label>Number of rows</Label>
                        <TextInput
                            type="number"
                            min="1"
                            max={MAX_ROWS}
                            autoFocus
                            placeholder="Enter number of rows"
                        />
                        <FieldError />
                    </TextField>
                </DialogBody>
                <DialogFooter>
                    <DialogCloseButton />
                    <Submit label="Insert" />
                </DialogFooter>
            </Form>
        </Dialog>
    );
}
